import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, formValueSelector, reduxForm, arrayRemove, arrayPush } from 'redux-form';
import type { FormErrors, InjectedFormProps } from 'redux-form';

import type { W_MaterialGroup_FindForCompany_VM_Single } from 'ab-api/web/materialGroup/findForCompany';

import type { RootState } from 'af-reducers';

import { MATERIAL_GROUP_LIST_EDIT } from 'af-constants/reduxForms';

import { Button, SegmentLabel } from '@acceligentllc/storybook';

import Input from 'af-fields/Input';

import SimpleTable from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';

import * as MaterialGroupActions from 'af-actions/materialGroup';

import type { MaterialGroupFM_single } from './formModel';
import MaterialGroupFM from './formModel';
import GroupActions from './GroupActions';
import styles from './styles.module.scss';

type Props = ConnectedProps<typeof connector> & InjectedFormProps<MaterialGroupFM>;

enum MaterialGroupType {
	NEW = 'NEW',
	EXISTING = 'EXISTING'
}

type MaterialGroupListItem =
	{ type: MaterialGroupType.NEW; data: { name: string; }; }
	| { type: MaterialGroupType.EXISTING; data: W_MaterialGroup_FindForCompany_VM_Single; };

const MaterialGroups: React.FC = (props: Props) => {
	const {
		createMaterialGroups,
		findCompanyMaterialGroups,
		newGroups,
		change: _change,
		handleSubmit,
		removeMaterialGroup,
		removeNewGroup,
		addNewGroup,
		valid,
	} = props;

	const newGroupsRef = React.useRef(newGroups);
	newGroupsRef.current = newGroups;

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const change = React.useMemo(() => _change, []);

	const [groups, setGroups] = React.useState<W_MaterialGroup_FindForCompany_VM_Single[]>([]);

	const loadGroups = React.useCallback(async () => {
		const data = await findCompanyMaterialGroups();
		change('existingNames', data.materialGroups.map((_group) => _group.name.toUpperCase()));

		setGroups(data.materialGroups);
	}, [change, findCompanyMaterialGroups]);

	React.useEffect(() => {
		loadGroups();
	}, [loadGroups]);

	const groupsForDisplay = React.useMemo<MaterialGroupListItem[]>(() => {
		const list: MaterialGroupListItem[] = [];

		list.push(...(newGroups ?? []).map((_group) => ({ type: MaterialGroupType.NEW as const, data: { name: _group.name } })));

		list.push(...groups.map((_group) => ({ type: MaterialGroupType.EXISTING as const, data: _group })));

		return list;
	}, [groups, newGroups]);

	const handleAddNewGroupClick = React.useCallback(() => {
		addNewGroup();
	}, [addNewGroup]);

	const saveMaterialGroups = React.useCallback(async (form: MaterialGroupFM) => {
		const names = form.groups.reduce<string[]>((_acc, _group) => {
			if (_group.name) {
				_acc.push(_group.name);
			}
			return _acc;
		}, []);
		await createMaterialGroups({ names });
		change('groups', []);
		await loadGroups();
	}, [change, createMaterialGroups, loadGroups]);

	const handleSaveClick = React.useMemo(() => handleSubmit(saveMaterialGroups), [handleSubmit, saveMaterialGroups]);

	const handleExistingRemove = React.useCallback(async (id: number) => {
		await removeMaterialGroup(id);
		await loadGroups();
	}, [loadGroups, removeMaterialGroup]);

	const renderActionsCell = React.useCallback((cell: CellContext<MaterialGroupListItem, number>) => {
		if (cell.row.original.type === MaterialGroupType.NEW) {
			return <GroupActions id={cell.row.index} onDelete={removeNewGroup} />;
		} else {
			return <GroupActions id={cell.row.original.data.id} onDelete={handleExistingRemove} />;
		}
	}, [handleExistingRemove, removeNewGroup]);

	const renderNameCell = React.useCallback((cell: CellContext<MaterialGroupListItem, string>) => {
		if (cell.row.original.type === MaterialGroupType.NEW) {
			const { index } = cell.row;
			return (
				<Field
					component={Input}
					name={`groups[${index}].name`}
					placeholder="Material Group Name"
				/>
			);
		}
		return cell.row.original.data.name.toUpperCase();
	}, []);

	const columns = React.useMemo<SimpleTableProps<MaterialGroupListItem>['columns']>(() => {
		return [
			{
				id: 'name',
				accessor: 'data',
				header: 'Name',
				cell: renderNameCell,
				enableSorting: true,
			},
			{
				id: 'actions',
				accessor: 'type',
				header: '',
				cell: renderActionsCell,
				size: 32,
			},
		];
	}, [renderActionsCell, renderNameCell]);

	const getRowId = React.useCallback((item: MaterialGroupListItem, index: number) => {
		return `${item.type}-${item.type === MaterialGroupType.NEW ? index : item.data.id}`;
	}, []);

	return (
		<>
			<div className={`form-box ${styles.MaterialGroups}`}>
				<div className={styles.titleActions}>
					<SegmentLabel
						label="Material Groups"
						tooltip="Groups used to organize and categorize related materials."
					/>
					<div className={styles.actions}>
						<Button
							customStyleClass={styles.action}
							icon="ActionPlusSmall"
							label="Add New Group"
							onClick={handleAddNewGroupClick}
							style="link"
							type="button"
						/>
						<Button
							customStyleClass={styles.action}
							disabled={!valid}
							label="Save"
							onClick={handleSaveClick}
							style="primary"
							type="button"
						/>
					</div>
				</div>
				<SimpleTable
					columns={columns}
					getRowId={getRowId}
					rows={groupsForDisplay}
				/>
			</div>
		</>
	);
};

const mapDispatchToProps = {
	createMaterialGroups: MaterialGroupActions.createMaterialGroups,
	findCompanyMaterialGroups: MaterialGroupActions.findCompanyMaterialGroups,
	removeMaterialGroup: MaterialGroupActions.removeMaterialGroup,
	addNewGroup: () => arrayPush(MATERIAL_GROUP_LIST_EDIT, 'groups', { name: '' }),
	removeNewGroup: (index: number) => arrayRemove(MATERIAL_GROUP_LIST_EDIT, 'groups', index),
};

const selectFormValue = formValueSelector(MATERIAL_GROUP_LIST_EDIT);

function mapStateToProps(state: RootState) {
	const newGroups: MaterialGroupFM_single[] = selectFormValue(state, 'groups');
	return { newGroups };
}
const connector = connect(mapStateToProps, mapDispatchToProps);

const INITIAL_VALUES = new MaterialGroupFM();

const formConnector = reduxForm<MaterialGroupFM, unknown, string | FormErrors>({
	form: MATERIAL_GROUP_LIST_EDIT,
	initialValues: INITIAL_VALUES,
	validate: MaterialGroupFM.validate,
});

const enhance = compose<React.ComponentType<unknown>>(
	React.memo,
	connector,
	formConnector
);

export default enhance(MaterialGroups);
